import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AaNgModule } from 'aa-ng';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-pagination-menu',
  imports: [AaNgModule, FormsModule],
  template: `
    <div class="flex">
      <div>
        @if (pageSizeOptions?.length) {
          {{ recordTypeName }} per page:
          <select
            class="form-control page-size-selection"
            aria-label="Page size selection"
            [(ngModel)]="pageSize"
          >
            @for (opt of pageSizeOptions; track opt) {
              <option [value]="opt">
                {{ opt }}
              </option>
            }
          </select>
        }
      </div>

      @if (numberOfPages > 1) {
        <aa-paginate
          [startPage]="1"
          [endPage]="numberOfPages"
          [(currentPage)]="currentPage"
        ></aa-paginate>
      }

      @if (numberOfRecords > 0) {
        <div>
          Showing {{ displayedRecordsRange }} of {{ numberOfRecords }}
          {{ recordTypeName }}
        </div>
      }
    </div>
  `,
  styleUrl: './pagination-menu.component.scss',
})
export class PaginationMenuComponent implements OnInit {
  @Input() set numberOfRecords(numRecords: number) {
    if (!!numRecords && numRecords !== this.numberOfRecords) {
      this.numberOfRecords$.next(numRecords);
    }
  }

  get numberOfRecords() {
    return this.numberOfRecords$.getValue();
  }

  @Input() set pageSize(pageSize: number) {
    if (+pageSize !== this.pageSize) {
      this.pageSize$.next(+pageSize);
    }
  }

  get pageSize() {
    return this.pageSize$.getValue();
  }

  @Input() set currentPage(currentPage: number) {
    if (currentPage !== this.currentPage) {
      this.currentPage$.next(currentPage);
    }
  }

  get currentPage() {
    return this.currentPage$.getValue();
  }

  @Input() pageSizeOptions = [5, 10, 15, 20, 25];
  @Input() recordTypeName = 'Records';

  @Output() pageSizeChange = new EventEmitter<number>();
  @Output() currentPageChange = new EventEmitter<number>(true);

  numberOfRecords$ = new BehaviorSubject(0);
  pageSize$ = new BehaviorSubject(10);
  currentPage$ = new BehaviorSubject(null);

  numberOfPages: number;
  displayedRecordsRange: string;

  ngOnInit() {
    combineLatest([
      this.numberOfRecords$,
      this.pageSize$,
      this.currentPage$.pipe(filter((x) => !!x)),
    ]).subscribe(([numberOfRecords, pageSize, currentPage]) => {
      this.numberOfPages = Math.ceil(numberOfRecords / pageSize);
      if (currentPage > this.numberOfPages) {
        currentPage = 1;
      }

      const firstDisplayedRecord = (currentPage - 1) * pageSize + 1;
      const lastDisplayedRecord = Math.min(
        numberOfRecords,
        firstDisplayedRecord + pageSize - 1,
      );
      this.displayedRecordsRange = `${firstDisplayedRecord} - ${lastDisplayedRecord}`;

      this.currentPageChange.emit(currentPage);
      this.pageSizeChange.emit(pageSize);
    });
  }
}
